import { Check, X as XIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from './Button';

/**
 * Property-Pair "Ist-Wert | Soll-Wert" mit Annehmen/Ablehnen-Buttons.
 *
 * Wird im Wartungsfenster-Diff-Drawer (WT-205 Carry-Over) verwendet:
 *  - Geänderte Felder werden visuell hervorgehoben (links muted/strike,
 *    rechts highlighted).
 *  - Pro Property gibt es Accept / Reject - Annehmen kopiert den Vorschlag
 *    in den Local-State des Eltern-Components.
 *
 * Wenn `changed === false` werden die Buttons disabled (es gibt nichts
 * zu übernehmen).
 */

export interface DiffFieldProps {
  label: string;
  current: ReactNode;
  proposed: ReactNode;
  changed: boolean;
  onAccept?: () => void;
  onReject?: () => void;
  /** Wenn true, wurde dieses Feld bereits per Accept übernommen. */
  accepted?: boolean;
  /** Wenn true, wurde dieses Feld per Reject explizit verworfen. */
  rejected?: boolean;
  hint?: ReactNode;
}

export function DiffField({
  label,
  current,
  proposed,
  changed,
  onAccept,
  onReject,
  accepted = false,
  rejected = false,
  hint,
}: DiffFieldProps): JSX.Element {
  const ariaState = accepted
    ? 'uebernommen'
    : rejected
      ? 'abgelehnt'
      : changed
        ? 'unterschiedlich'
        : 'identisch';
  return (
    <div
      className="grid grid-cols-1 gap-3 rounded-md border border-border p-3 md:grid-cols-[1fr_1fr_auto]"
      aria-label={`${label}: ${ariaState}`}
    >
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-muted">{label}</p>
        <div
          className={[
            'mt-1 text-sm font-mono',
            changed && !accepted ? 'text-muted line-through decoration-muted' : 'text-text',
          ].join(' ')}
          data-testid={`diff-current-${slug(label)}`}
        >
          {renderValue(current)}
        </div>
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-muted">Vorschlag</p>
        <div
          className={[
            'mt-1 rounded-md px-2 py-1 text-sm font-mono',
            changed && !rejected
              ? 'bg-primary/10 text-primary'
              : 'text-muted',
          ].join(' ')}
          data-testid={`diff-proposed-${slug(label)}`}
        >
          {renderValue(proposed)}
        </div>
        {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
      </div>
      <div className="flex items-start gap-1">
        {onAccept && (
          <Button
            size="sm"
            variant={accepted ? 'primary' : 'secondary'}
            disabled={!changed || accepted}
            leftIcon={<Check className="size-3" />}
            onClick={onAccept}
            aria-label={`${label}: Vorschlag übernehmen`}
          >
            {accepted ? 'Übernommen' : 'Übernehmen'}
          </Button>
        )}
        {onReject && (
          <Button
            size="sm"
            variant="ghost"
            disabled={!changed || rejected}
            leftIcon={<XIcon className="size-3" />}
            onClick={onReject}
            aria-label={`${label}: Vorschlag ablehnen`}
          >
            Ablehnen
          </Button>
        )}
      </div>
    </div>
  );
}

function renderValue(value: ReactNode): ReactNode {
  if (value === null || value === undefined || value === '') return <span className="text-muted">–</span>;
  return value;
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
